import { useState } from "react"
import { Box, Button, Stack, Typography } from "@mui/material"
import { DATE_FORMATS } from "lib/@constants/date-format"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { hasAmendsContent } from "authoring/routes/project/amends/lib/amends-content"
import { buildContentPreview, hasLongContent } from "authoring/routes/project/amends/lib/amends-content-preview"
import Iconify from "minimals-template/components/Iconify"
import { htmlToText } from "lib/html-to-text"

export function AuthoringEditorInlineRejectionBanner({ decision, title = "Change Rejected", outlined = false }) {
    const [expanded, setExpanded] = useState(false)

    if (!decision?.payload?.status) return null

    const reason = decision?.payload?.reason ?? ""
    const reviewerName = decision?.userName ?? ""
    const dateLabel = decision?.date ? new Date(decision.date).format(DATE_FORMATS.long) : ""
    const hasReason = hasAmendsContent(reason)
    const textContent = hasReason ? htmlToText(reason, true).trim() : ""
    const previewContent = hasReason ? buildContentPreview(reason) : ""
    const isLongContent = hasReason && hasLongContent(reason)
    const displayContent = expanded || !isLongContent ? textContent : previewContent

    return (
        <Stack spacing={0.5}>
            <If truthy={Boolean(reviewerName || dateLabel)}>
                <Stack direction="row" alignItems="center" sx={{ px: 0.25 }}>
                    <Typography variant="caption" sx={{ color: "text.secondary", flex: 1 }}>
                        {reviewerName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", flexShrink: 0 }}>
                        {dateLabel}
                    </Typography>
                </Stack>
            </If>
            <Box
                sx={{
                    ...getRejectionSurfaceSx(outlined),
                    borderRadius: 2,
                    p: 1.5,
                }}
                data-cy="authoring-editor-rejection-banner"
            >
                <Stack direction="row" spacing={1} alignItems="center">
                    <Iconify icon="mdi:close-circle" sx={{ color: "error.main" }} />
                    <Typography variant="subtitle2" sx={{ color: "error.main", flex: 1 }}>
                        {title}
                    </Typography>
                </Stack>
                <If truthy={hasReason}>
                    <Typography
                        data-cy="authoring-editor-rejection-banner-content"
                        variant="body2"
                        sx={{
                            mt: 0.75,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                        }}
                    >
                        {displayContent}
                    </Typography>
                </If>
                <If truthy={isLongContent}>
                    <Button
                        variant="text"
                        size="small"
                        onClick={handleToggleExpanded}
                        data-cy="authoring-editor-rejection-banner-toggle"
                        sx={{ mt: 0.5, ml: -1, alignSelf: "flex-start" }}
                    >
                        <If truthy={expanded}>
                            Show less
                            <Else>Show more</Else>
                        </If>
                    </Button>
                </If>
            </Box>
        </Stack>
    )

    function handleToggleExpanded() {
        setExpanded(!expanded)
    }
}

// A carried-forward rejection from a previous cycle is history, not a live decision, so it
// reads as an outlined box rather than the filled surface used for the current cycle.
function getRejectionSurfaceSx(outlined) {
    if (!outlined) return { bgcolor: "error.lighter" }
    return {
        bgcolor: "background.paper",
        border: 1,
        borderColor: "error.main",
    }
}
